import { logger } from '@/utils/logger';
// Bot Detection System for Automation Tools
// Detects Selenium, Playwright, Cypress, and other automation frameworks

export interface BotDetectionResult {
  isBot: boolean;
  detectedTools: string[];
  riskScore: number;
}

// Comprehensive bot detection function
export function detectAutomationBots(): BotDetectionResult {
  const detectedTools: string[] = [];
  let riskScore = 0;

  // 1. Check for Selenium WebDriver
  if (
    (window as any).webdriver !== undefined ||
    (window as any).__webdriver_evaluate !== undefined ||
    (window as any).__selenium_evaluate !== undefined ||
    (window as any).__webdriver_script_function !== undefined ||
    (window as any).__webdriver_script_func !== undefined ||
    (window as any).__webdriver_script_fn !== undefined ||
    (window as any).__fxdriver_evaluate !== undefined ||
    (window as any).__driver_unwrapped !== undefined ||
    (window as any).__webdriver_unwrapped !== undefined ||
    (window as any).__driver_evaluate !== undefined ||
    (window as any).__fxdriver_unwrapped !== undefined ||
    (window as any).__webdriver_evaluate !== undefined ||
    navigator.webdriver === true
  ) {
    detectedTools.push('Selenium WebDriver');
    riskScore += 100; // Definitive bot detection
  }

  // 2. Check for Puppeteer
  if (
    (window as any)._phantom !== undefined ||
    (window as any).__phantom !== undefined ||
    (window as any).callPhantom !== undefined ||
    (window as any).phantom !== undefined
  ) {
    detectedTools.push('Puppeteer/PhantomJS');
    riskScore += 100;
  }

  // 3. Check for Playwright
  if (
    (window as any).__playwright !== undefined ||
    (window as any).__pw_manual !== undefined ||
    (navigator.userAgent.includes('HeadlessChrome') && navigator.plugins.length === 0)
  ) {
    detectedTools.push('Playwright');
    riskScore += 100;
  }

  // 4. Check for Cypress
  if (
    (window as any).Cypress !== undefined ||
    (window as any).cy !== undefined ||
    (window as any).__cypress !== undefined ||
    window.location.href.includes('__cypress')
  ) {
    detectedTools.push('Cypress');
    riskScore += 100;
  }

  // 5. Check for headless browsers
  if (navigator.webdriver === true) {
    detectedTools.push('Headless Browser');
    riskScore += 90;
  }

  // 6. Check for missing plugins (common in headless browsers) - reduced penalty
  if (navigator.plugins.length === 0) {
    detectedTools.push('No Browser Plugins');
    riskScore += 15; // Reduced from 30 since some browsers legitimately have no plugins
  }

  // 7. Check for missing languages
  if (navigator.languages.length === 0) {
    detectedTools.push('No Languages');
    riskScore += 30;
  }

  // 8. Check for automated user agent patterns
  const suspiciousAgents = [
    'headless',
    'phantom',
    'selenium',
    'webdriver',
    'automation',
    'bot',
    'crawler',
    'scraper'
  ];
  
  if (suspiciousAgents.some(agent => navigator.userAgent.toLowerCase().includes(agent))) {
    detectedTools.push('Suspicious User Agent');
    riskScore += 80;
  }

  // 9. Check for automation-specific global variables
  const automationGlobals = [
    '__nightmare',
    'nightmare',
    '__chrome_webdriver_',
    '__webdriver_script_fn',
    'webdriver_script_function',
    'domAutomation',
    'domAutomationController'
  ];

  for (const global of automationGlobals) {
    if ((window as any)[global] !== undefined) {
      detectedTools.push('Automation Globals');
      riskScore += 70;
      break;
    }
  }

  // 10. Check for Chrome automation indicators
  if (
    navigator.userAgent.includes('Chrome') &&
    (window as any).chrome &&
    !(window as any).chrome.runtime
  ) {
    detectedTools.push('Chrome Automation');
    riskScore += 60;
  }

  // 11. Check for permission API behavior (bots often have different permission states)
  try {
    if (navigator.permissions) {
      // This is more for future enhancement - permissions API can reveal automation
      // For now, we'll skip this as it requires async handling
    }
  } catch (_e) {
    // Permission API not available or restricted - could indicate automation
    riskScore += 20;
  }

  // 12. Check for screen resolution abnormalities - only flag zero values
  if (
    screen.width === 0 || 
    screen.height === 0 ||
    screen.availWidth === 0 ||
    screen.availHeight === 0
  ) {
    detectedTools.push('Invalid Screen Resolution');
    riskScore += 25; // Reduced penalty
  }

  // 13. Check for missing touch events (mobile bots)
  if (navigator.maxTouchPoints === 0 && 'ontouchstart' in window) {
    detectedTools.push('Inconsistent Touch Support');
    riskScore += 20;
  }

  // 14. Advanced: Check for iframe context (some bots run in iframes)
  if (window.top !== window.self) {
    riskScore += 10; // Minor increase, as legitimate uses exist
  }

  // 15. Check for automated mouse/keyboard patterns (this would need to be implemented with event listeners)
  // This is more complex and would require tracking user interactions over time

  return {
    isBot: riskScore >= 150, // More lenient threshold - only flag obvious bots
    detectedTools,
    riskScore
  };
}

// Simple check function for form validation
export function isAutomationBot(): boolean {
  const result = detectAutomationBots();
  return result.isBot;
}

// Get detailed bot detection info for logging
export function getBotDetectionInfo(): BotDetectionResult {
  return detectAutomationBots();
}

// Human-readable error messages
export function getBotDetectionError(): string {
  const result = detectAutomationBots();
  
  if (!result.isBot) {
    return '';
  }

  if (result.detectedTools.includes('Selenium WebDriver')) {
    return 'Automated submission detected. Please use a regular browser to submit this form.';
  }
  
  if (result.detectedTools.includes('Cypress')) {
    return 'Test automation detected. This form cannot be submitted during automated testing.';
  }
  
  if (result.detectedTools.includes('Playwright')) {
    return 'Browser automation detected. Please disable automation tools and submit manually.';
  }
  
  if (result.detectedTools.includes('Puppeteer/PhantomJS')) {
    return 'Headless browser detected. Please use a standard browser to submit this form.';
  }

  // Generic message for other detected automation
  return 'Automated submission detected. Please ensure you are using a standard browser and submit the form manually.';
}

// Initialize bot detection on page load - SIMPLIFIED
export function initializeBotDetection(): void {
  // Simplified bot detection to avoid performance issues
  try {
    if (process.env.NODE_ENV === 'development') {
      // Quick check only
      const isBot = navigator.webdriver === true || (window as any).webdriver !== undefined;
      if (isBot) {
        logger.warn('🤖 Basic automation detected');
      } else {
        logger.debug('✅ Basic check passed');
      }
    }
  } catch (error) {
    logger.debug('Bot detection initialization error:', error);
  }
}

// Enhanced detection that can be run periodically
export function runContinuousBotDetection(callback: (isBot: boolean) => void): () => void {
  const checkBot = () => {
    const isBot = isAutomationBot();
    callback(isBot);
  };
  
  // Initial check
  checkBot();
  
  // Check every 5 seconds
  const intervalId = setInterval(checkBot, 5000);
  
  // Return cleanup function
  return () => {
    clearInterval(intervalId);
  };
}

// Additional validation for form submissions
export function validateHumanSubmission(): { 
  isValid: boolean; 
  error: string; 
  details: BotDetectionResult 
} {
  const detectionResult = detectAutomationBots();
  
  return {
    isValid: !detectionResult.isBot,
    error: detectionResult.isBot ? getBotDetectionError() : '',
    details: detectionResult
  };
}